// @ts-nocheck
import {
  ClipboardList,
  ShieldCheck,
  ShieldAlert,
  BookOpen,
  Users,
  StickyNote,
  Settings,
  ChevronRight,
  CircleHelp,
} from "lucide-react";
import "./KitchenShell.css";
import { useBeheerPin } from "./BeheerPin";
import { useT } from "../i18n";

function Card({ kind, title, Icon, onOpen, onHelp, children }) {
  const { t } = useT();
  return (
    <section className={`kc-card kc-card--${kind}`}>
      <div className="kc-card__accent" />
      <div className="kc-card__body">
        <div className="kc-card__head">
          <div className="kc-card__identity">
            <div className="kc-card__icon"><Icon size={20} /></div>
            <div className="kc-card__title">{title}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            {onHelp && (
              <button type="button" onClick={onHelp} title={`${t("Hulp over")} ${title}`} aria-label={`${t("Hulp over")} ${title}`}
                style={{ width: 32, height: 32, display: "grid", placeItems: "center", borderRadius: "50%", border: "1px solid #E2E8F0", background: "#fff", color: "#64748B", cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: 15, fontWeight: 850 }}>
                ?
              </button>
            )}
            {onOpen && (
              <button type="button" className="kc-card__link" onClick={onOpen}>
                {t("Open")} <ChevronRight size={13} style={{ verticalAlign: -2 }} />
              </button>
            )}
          </div>
        </div>
        {children}
      </div>
    </section>
  );
}

function initials(name = "") {
  const delen = String(name).trim().split(/\s+/).filter(Boolean);
  if (!delen.length) return "?";
  return (delen[0][0] + (delen.length > 1 ? delen[delen.length - 1][0] : "")).toUpperCase();
}

function statusTekst(value, positief, negatief, onbekend) {
  if (value === true) return positief;
  if (value === false) return negatief;
  return onbekend;
}

function abonnementTekst(status, plan, t) {
  const s = String(status || "").toLowerCase();
  const p = String(plan || "").toLowerCase();
  const planTekst = p === "monthly" ? t("maandelijks") : p === "yearly" ? t("jaarlijks") : "";

  if (s === "active") return [t("actief"), planTekst].filter(Boolean).join(" · ");
  if (s === "internal") return t("intern");
  if (s === "trial") return t("proefperiode");
  if (s === "past_due") return t("betaling openstaand");
  if (s === "cancelled") return t("opgezegd");
  if (s === "expired") return t("verlopen");
  return t("status onbekend");
}

function abonnementStijl(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active" || s === "internal") return { color: "#15803D" };
  if (s === "trial" || s === "past_due") return { color: "#B45309" };
  if (s === "cancelled" || s === "expired") return { color: "#DC2626" };
  return undefined;
}

export default function DashboardV2({
  userNaam = "",
  mep = { open: 0, klaar: 0, totaal: 0 },
  recepten = { totaal: 0, laatst: "" },
  haccp = { dagelijksOpen: 0, wekelijksOpen: 0, vierwekelijksOpen: 0 },
  team = [],
  whiteboard = [],
  system = {},
  onNavigate,
  onOpenWhiteboard,
  onOpenHelp,
  language = {},
}) {
  const { pinConfigured } = useBeheerPin();
  const { t } = useT();
  const mepPct = mep.totaal ? Math.round((mep.klaar / mep.totaal) * 100) : 0;
  const naam = userNaam ? `, ${userNaam}` : "";
  const wbPreview = whiteboard.slice(0, 4);

  return (
    <main className="kc-main">
      <header className="kc-dashboard__header">
        <div>
          <h1 className="kc-dashboard__title">{t("Goedendag")}{naam}</h1>
          <p className="kc-dashboard__subtitle">{t("Dit vraagt vandaag aandacht in de keuken.")}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {language?.languages?.length > 0 && (
            <select
              value={language.lang || "nl"}
              onChange={(e) => language.onChange?.(e.target.value)}
              aria-label={language.label || "Taal"}
              title={language.label || "Taal"}
              style={{ fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, color: "#334155", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 9, padding: "7px 9px", cursor: "pointer" }}
            >
              {language.languages.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          )}
          {onOpenHelp && (
            <button type="button" className="kc-dashboard__help" onClick={() => onOpenHelp?.(null)} title={t("Hulp")} aria-label={t("Hulp openen")}>
              <CircleHelp size={18} />
              <span>{t("Hulp")}</span>
            </button>
          )}
        </div>
      </header>

      <button
        type="button"
        onClick={() => onNavigate?.("settings")}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 12,
          textAlign: "left",
          fontFamily: "inherit",
          cursor: "pointer",
          padding: "12px 14px",
          marginBottom: 16,
          borderRadius: 12,
          border: `1px solid ${pinConfigured ? "#BBF7D0" : "#FECACA"}`,
          background: pinConfigured ? "#F0FDF4" : "#FEF2F2",
          color: "#0F172A",
        }}
      >
        <div style={{
          width: 34,
          height: 34,
          borderRadius: 9,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: pinConfigured ? "#DCFCE7" : "#FEE2E2",
          color: pinConfigured ? "#15803D" : "#DC2626",
        }}>
          {pinConfigured ? <ShieldCheck size={18} /> : <ShieldAlert size={18} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800 }}>
            {pinConfigured ? t("Dit account is beveiligd met een beheer-pincode") : t("Deze KitchenControl-omgeving is nog niet beveiligd")}
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, marginTop: 2, color: "#64748B" }}>
            {pinConfigured
              ? t("Wijzigingen en verwijderingen zijn beschermd. Klik om de beheer-pincode te beheren.")
              : t("Iedereen die deze login gebruikt kan ook wijzigingen en verwijderingen uitvoeren. Stel een beheer-pincode in als dit een gedeelde tablet is.")}
          </div>
        </div>
        <ChevronRight size={17} color="#64748B" style={{ flexShrink: 0 }} />
      </button>

      <div className="kc-dashboard__grid">
        <Card kind="mep" title={t("Mise en Place")} Icon={ClipboardList} onOpen={() => onNavigate?.("mep", "lijst")} onHelp={() => onOpenHelp?.("mep")}>
          <div className="kc-card__value">{mep.open}</div>
          <div className="kc-card__meta">{t("taken open")} · {mep.klaar} {t("van")} {mep.totaal} {t("klaar")}</div>
          <div className="kc-progress" aria-label={`${mepPct}% ${t("voltooid")}`}>
            <span style={{ width: `${mepPct}%` }} />
          </div>
        </Card>

        <Card kind="haccp" title="HACCP" Icon={ShieldCheck} onOpen={() => onNavigate?.("haccp")} onHelp={() => onOpenHelp?.("haccp")}>
          <div className="kc-status-list">
            <div className="kc-status-row"><span>{t("Dagelijks")}</span><strong>{haccp.dagelijksOpen} {t("open")}</strong></div>
            <div className="kc-status-row"><span>{t("Wekelijks")}</span><strong>{haccp.wekelijksOpen} {t("open")}</strong></div>
            <div className="kc-status-row"><span>{t("4-wekelijks")}</span><strong>{haccp.vierwekelijksOpen} {t("open")}</strong></div>
          </div>
        </Card>

        <Card kind="recipes" title={t("Recepten")} Icon={BookOpen} onOpen={() => onNavigate?.("recepten", "recepten")} onHelp={() => onOpenHelp?.("recepten")}>
          <div className="kc-card__value">{recepten.totaal}</div>
          <div className="kc-card__meta">
            {recepten.laatst ? `${t("Laatst")}: ${recepten.laatst}` : t("ingevoerde recepten")}
          </div>
        </Card>

        <Card kind="team" title={t("Team")} Icon={Users} onOpen={() => onNavigate?.("team", "medewerkers")} onHelp={() => onOpenHelp?.("team")}>
          <div className="kc-card__value">{team.length}</div>
          <div className="kc-card__meta">{t("teamleden")}</div>
          {team.length > 0 && (
            <div className="kc-avatar-row" aria-label="Team">
              {team.slice(0, 7).map((persoon, index) => (
                <div key={persoon.id || `${persoon.naam}-${index}`} className="kc-avatar" title={persoon.naam}>
                  {persoon.initialen || initials(persoon.naam)}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card kind="whiteboard" title={t("Whiteboard")} Icon={StickyNote} onOpen={onOpenWhiteboard} onHelp={() => onOpenHelp?.("whiteboard")}>
          <div className="kc-card__value">{whiteboard.length}</div>
          <div className="kc-card__meta">{t("open items")}</div>
          {wbPreview.length > 0 && (
            <div className="kc-whiteboard-lines">
              {wbPreview.map((item, index) => (
                <div className="kc-whiteboard-line" key={item.id || index}>
                  <span>{item.naam || item.tekst || item.product || "Item"}</span>
                  {item.via && <small>{t("via:")} {item.via}</small>}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card kind="system" title="KitchenControl" Icon={Settings} onOpen={() => onNavigate?.("settings")} onHelp={() => onOpenHelp?.("settings")}>
          <div className="kc-system-grid">
            <div className="kc-system-pill" style={system.printer === true ? { color: "#15803D" } : undefined}>{t("Printer")} {statusTekst(system.printer, t("verbonden"), t("niet verbonden"), t("status onbekend"))}</div>
            <div className="kc-system-pill" style={system.printbox === true ? { color: "#15803D" } : undefined}>{t("Printkastje")} {statusTekst(system.printbox, t("online"), t("offline"), t("status onbekend"))}</div>
            <div className="kc-system-pill" style={abonnementStijl(system.subscriptionStatus)}>{t("Abonnement")} {abonnementTekst(system.subscriptionStatus, system.subscriptionPlan, t)}</div>
            <div className="kc-system-pill">{system.teamCount ?? team.length} {t("teamleden")}</div>
          </div>
        </Card>
      </div>
    </main>
  );
}
